import { html, LitElement } from 'lit';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { state, property, query } from 'lit/decorators.js';
import { LicenseRenderers } from '../../../../company/common/licenseRenderers/LicenseRenderers';
import { FormGroupValue } from '../../../../ui/gzForm/formGroup/types/form-group-value';
import { CompanyFeatures } from '../../../../company/types/company-data';
import { GzToggleWebComponent } from '../../../../ui/gzToggle';
import { FontSizes, LineHeights } from '../../../../types/common/types';
import { WEB_COMPONENT_GZ_TOGGLE } from '../../../../webcomponents.constants';
import { styles } from '../view/styles';
import { CustomizableValidator } from '../../../../ui/gzForm/validators/customizable.validator';
import { ValidationResult } from '../../../../ui/gzForm/formField/types/form-validator';
import { translateFn } from '../../../../utils/translator.helper';
import { GzInfoBoxWebComponent } from '../../../../ui/gzInfoBox';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { Events } from '../../../../ui/gzInfoBox/types';
import { WebComponentEvent } from '../../../../framework/BaseWebComponentController';
import { AvailableBundles } from '../../../types/Bundles';
import { CSSResultGroup } from 'lit/development';
import { Observable } from 'rxjs';

export class LicenseMonthlyResellBundleFormWebComponent extends LitElement {
  static get styles(): CSSResultGroup {
    return styles;
  }

  constructor() {
    super();

    this.addEventListener(Events.INFO_BOX_UPDATED, (event: WebComponentEvent) => {
      const infoBoxContainer = event.detail.infoBox.shadowRoot.querySelector('.info-box-container');
      infoBoxContainer.style.width = '100%';
    });
  }

  public get value(): FormGroupValue {
    return this.formGroup.value;
  }

  public get valid(): Observable<boolean> {
    return this.formGroup.valid$;
  }

  /**
   * To be changed CompanyFeatures -> BundleFeatures
   * @param {CompanyFeatures} formData
   */
  public set value(formData: CompanyFeatures) {
    this.formGroup.value = formData;
    for (const [companyFeature, value] of Object.entries(formData)) {
      const bundleToggle = this.renderRoot.querySelector(
        WEB_COMPONENT_GZ_TOGGLE + '[data-resell-feature="' + companyFeature + '"]'
      ) as GzToggleWebComponent;
      if (bundleToggle) {
        bundleToggle.checked = value;
      }
    }
  }

  public set disable(disable: boolean) {
    this.disabled = disable;
  }

  public get disable(): boolean {
    return this.disabled;
  }

  @state()
  private formGroup: FormGroup = new FormGroup({});

  @query('#at-least-one-selected')
  errorBox: GzInfoBoxWebComponent;

  @property({
    type: Array,
    attribute: true,
    reflect: true,
  })
  public availableBundles: AvailableBundles | undefined;
  private disabled: boolean;

  private static renderBundlesDetails(availableBundles) {
    if (Object.keys(availableBundles).length === 0) {
      return html``;
    }
    const bundlesDescription = LicenseRenderers.getBundlesDescription();
    return html` <gz-text
      fontSize="${FontSizes.HEADING_6}"
      lineHeight="${LineHeights.NORMAL}"
      class="bundles-details-text"
      id="own-use-bundles-details"
    >
      ${bundlesDescription[0]}
      <gz-link type="underline" color="primary" id="resell-bundles-details-link" href=${bundlesDescription[1]}>
        ${bundlesDescription[2]}
      </gz-link>
    </gz-text>`;
  }

  public validate(): void {
    this.formGroup.validate();
  }

  shouldUpdate(): boolean {
    return this.availableBundles !== undefined;
  }

  updated(_changedProperties): void {
    this.updateComplete.then(() => {
      if (this.availableBundles) {
        for (const index in this.availableBundles) {
          if (!this.availableBundles.hasOwnProperty(index)) {
            continue;
          }
          const bundleFormField = this.renderRoot.querySelector(
            '#form-field-toggle-' + index
          ) as GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
          bundleFormField.Validators = [CustomizableValidator(this.customValidation.bind(this))];

          if (bundleFormField) {
            this.formGroup.addFormChild([this.availableBundles[index].delegateFeature], bundleFormField);
          }
        }
      }
    });
  }

  /**
   * We need to check that the user has at least one bundle selected for resell
   * @private
   */
  private customValidation(): ValidationResult {
    const hasAtLeastOneBundle = Object.values(this.formGroup.value).some(value => value === true);
    if (hasAtLeastOneBundle || this.disabled) {
      toggleVisibility(this.errorBox, false);
      return {
        valid: true,
      };
    }

    toggleVisibility(this.errorBox, true);
    return {
      valid: false,
      error: '',
    };
  }

  private handleChange(e): void {
    const event = new CustomEvent('toggleResellBundleChanged', {
      detail: {
        checked: e.detail.checked,
        bundleId: e.target.getAttribute('data-bundle-id'),
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  render() {
    return html`
      <div id="wrapper">
        ${this.renderToggleList(this.availableBundles)}
        <gz-info-box class="hidden" severity="error" id="at-least-one-selected">
          ${translateFn('You need to select at least one protection model.')}
        </gz-info-box>
        ${LicenseMonthlyResellBundleFormWebComponent.renderBundlesDetails(this.availableBundles)}
      </div>
    `;
  }

  private renderToggleList(availableBundles) {
    return html`${Object.keys(availableBundles).map(index => {
      return html` <gz-form-field id="form-field-toggle-${index}">
        <gz-toggle
          data-bundle-id="${index}"
          data-resell-feature="${availableBundles[index].delegateFeature}"
          @toggleChange="${this.handleChange}"
          gz-form-input
        >
          ${LicenseRenderers.getTranslatedBundleName(Number(index))}
        </gz-toggle>
        <gz-text
          fontSize="${FontSizes.PARAGRAPH_12}"
          lineHeight="${LineHeights.NORMAL}"
          class="helper-text mb-1"
          id="description-for-bundle-${index}"
        >
          ${LicenseRenderers.getTranslatedBundleDescription(Number(index))}
        </gz-text>
      </gz-form-field>`;
    })}`;
  }
}
